import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { CmsPage } from '../../models/content';
import { contentSlug } from '../../content-slug';
import { ModalService } from '../../services/modal.service';
import { pageBlockRequest } from './block-preview/block-preview-request';
import { BlockPreviewSettingsService } from './block-preview/block-preview-settings.service';
import { BlockPreviewSource, SectionsEditorSource } from './block-preview/block-preview-source';
import { ContentSectionsEditorComponent } from './content-sections-editor.component';

/**
 * CMS page editor (PAGE-CMS.md phase 2) — page metadata wrapped around the
 * shared sections editor (content-sections-editor.component, which the
 * Region editor's "Page layout" tab embeds too).
 *
 * This component owns the PAGE-level concerns: title/address/description,
 * publish + noindex, address validation (the locations-area rules, dynamic
 * route subtrees, slug collisions) and rename → redirect recording. The
 * sections editor owns everything inside the section list.
 */

/**
 * The region tree's generated third segments (picaluna-v3 src/lib/urls.ts:
 * SERVICE_SEGMENTS + CANONICAL_REGION_SERVICES).
 *
 * These have a BUILT-IN TEMPLATE, they are not forbidden (PAGE-CMS.md §12,
 * issue #205). A published page at one of these addresses replaces the
 * generated page; delete or unpublish it and the template renders again —
 * the same rule region hubs and suburb pages already follow. The set is kept
 * so the editor can SAY that is what is happening. Mirror any change to
 * urls.ts here AND in the sync's validation.
 *
 * funeral-directors, direct-cremation and memorial-services are no longer
 * generated (one page per suburb, #257): the site 301s them to
 * funeral-services, and validatePath refuses a page there.
 */
const RESERVED_REGION_SEGMENTS = new Set([
  'funeral-services',
  'cremations',
  'burial',
  'funeral-cost',
  'cremation-costs',
  'venues',
]);

@Component({
  selector: 'app-content-page',
  templateUrl: './content-page.component.html',
})
export class ContentPageComponent implements OnInit {
  page!: CmsPage;
  isNew = false;
  /** True while the save request is in flight: the button pressed spins, the others lock. */
  saving = false;
  /** Which button that was: Save & Close (true) or Save. */
  closing = false;
  form!: FormGroup;
  /** Every OTHER page's address — for the collision check in validatePath. */
  otherPagePaths: string[] = [];
  /** The site's regions (slug + state) — for the §9.1 bespoke-path check. */
  regions: Array<{ slug: string; state?: string }> = [];
  /** Suburb designed-page paths (no leading slash) — the allowed 4-segment forms. */
  suburbPaths: string[] = [];
  /** The address this page had when the editor opened — see onSave's redirect. */
  originalPath = '';
  /** When the last Save (not Save & Close) landed; shown beside the buttons. */
  lastSavedAt: Date | null = null;

  @ViewChild(ContentSectionsEditorComponent, { static: true })
  sectionsEditor!: ContentSectionsEditorComponent;

  /** The block being edited, at the address typed (#263). */
  previewSource!: BlockPreviewSource;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public preview: BlockPreviewSettingsService,
    private fb: FormBuilder,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.page = this.config.data.page;
    this.isNew = !!this.config.data.isNew;
    this.otherPagePaths = this.config.data.otherPagePaths ?? [];
    this.regions = this.config.data.regions ?? [];
    this.suburbPaths = this.config.data.suburbPaths ?? [];
    this.originalPath = this.page.path ?? '';

    this.form = this.fb.group({
      title: new FormControl(this.page.title ?? '', [Validators.required]),
      path: new FormControl(
        { value: this.page.path ?? '', disabled: this.page.locked === true },
        [Validators.required, Validators.pattern(/^\/[a-z0-9\-/]*$/)]
      ),
      // Required: an empty description ships <meta name="description" content="">
      // on a live indexable page, and nothing downstream catches it. The
      // pattern rejects whitespace-only, which onSave would otherwise trim to
      // null and store as "no description" while the form looked satisfied.
      description: new FormControl(this.page.description ?? '', [
        Validators.required,
        Validators.pattern(/\S/),
      ]),
      published: new FormControl(this.page.published === true),
      noindex: new FormControl(this.page.noindex === true),
    });

    this.sectionsEditor.initFrom(this.page.sections);
    this.previewSource = new SectionsEditorSource(this.sectionsEditor, (sections, active) =>
      pageBlockRequest(this.form.getRawValue().path, sections, active)
    );
  }

  // ── Save ────────────────────────────────────────────────────────────────

  /** Save keeps the editor open; Save & Close closes it once the server confirms. */
  onSave(close: boolean): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // getRawValue: `path` is disabled on locked pages and would otherwise be
    // dropped from form.value — wiping the path on every save.
    const value: any = { ...this.form.getRawValue() };

    const problem = this.validatePath() ?? this.sectionsEditor.validate(value.published === true);
    if (problem) {
      this.modalService.generic({
        title: "This page can't be saved yet",
        copy: [problem],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }

    const sections = this.sectionsEditor.toMap();

    // Renaming a page that has already been on the site must not orphan its
    // old address. Record it; the site turns these into 301s.
    const canon = (v: string) => (String(v || '').trim().replace(/\/+$/, '') || '/');
    const previousPath = canon(this.originalPath);
    const newPath = canon(value.path);
    const redirectFrom = [...(this.page.redirectFrom ?? [])].map(canon);

    // Recorded for ANY rename of an existing page, not only one that was
    // published through the per-page cloud button. publishedSlug is only set
    // by that button, so gating on it silently orphaned the old URL of a page
    // that went live through a full publish — the common case. A redirect for
    // an address nobody visited is harmless; a missing one is a dead link.
    if (!this.isNew && previousPath && previousPath !== newPath) {
      if (!redirectFrom.includes(previousPath)) redirectFrom.push(previousPath);
    }
    // Canonical compare: a page that has moved BACK to an address it used
    // before must not redirect to itself (an infinite 301).
    const cleanedRedirects = [...new Set(redirectFrom)].filter((r) => r !== newPath);

    Object.assign(this.page, {
      title: value.title,
      path: value.path,
      description: (value.description || '').trim() || null,
      published: value.published === true,
      noindex: value.noindex === true,
      ...(cleanedRedirects.length ? { redirectFrom: cleanedRedirects } : {}),
      sections,
    });

    // The dialog stays open (Save spinning) until the server confirms, so
    // Publish can't race an in-flight write; Save & Close closes only then.
    // On rejection the dialog stays open with everything typed — fix and
    // retry, nothing lost.
    const save = this.config.data.save as ((p: CmsPage) => any) | undefined;
    if (!save) {
      this.ref.close({ saved: true, page: this.page });
      return;
    }
    this.saving = true;
    this.closing = close;
    save(this.page).subscribe({
      next: () => {
        if (close) {
          this.ref.close({ saved: true, page: this.page, persisted: true });
          return;
        }
        // Still open: what was just saved is now the stored page, so the next
        // save compares against it (a later rename records THIS address).
        this.saving = false;
        this.isNew = false;
        this.originalPath = this.page.path ?? '';
        this.form.markAsPristine();
        this.lastSavedAt = new Date();
      },
      error: (err: any) => {
        this.saving = false;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }

  /**
   * Page-level address checks (the sections editor validates its own list):
   * a path that collides with another page's after slugification makes a
   * publish delete one of the two, and the locations/dynamic-route areas
   * have their own rules.
   */
  private validatePath(): string | null {
    const value = this.form.getRawValue();
    const path = value.path as string;

    // The locations area (/nsw, /vic, /qld…) is built from the Regions and
    // Suburbs data. Two shapes are allowed inside it (SERVICES-CMS.md §9.1):
    // a region hub's own address (an override of the built-in region page),
    // and /{state}/{region}/{your-page} whose last segment is not generated.
    const STATE_CODES = ['nsw', 'vic', 'qld', 'act', 'wa', 'sa', 'tas', 'nt'];
    const segments = (path || '').split('/').filter(Boolean);
    if (STATE_CODES.includes(segments[0])) {
      const isHubOverride =
        segments.length === 2 &&
        this.regions.some(
          (r) => r.slug === segments[1] && (r.state || 'nsw').toLowerCase() === segments[0]
        );
      // One page per suburb (#257): the site 301s a region's or suburb's
      // funeral-directors, direct-cremation and memorial-services addresses to
      // funeral-services, so a page there would never be seen.
      const RETIRED_SERVICE_SEGMENTS = ['funeral-directors', 'direct-cremation', 'memorial-services'];
      if ((segments.length === 3 || segments.length === 4) && RETIRED_SERVICE_SEGMENTS.includes(segments[2])) {
        const target = `/${segments[0]}/${segments[1]}/funeral-services${segments[3] ? `/${segments[3]}` : ''}`;
        return `/${segments.join('/')} now redirects to ${target}, so a page here would never be seen. Use ${target} instead${segments[3] ? ' (a suburb has one page, designed from the Suburbs list)' : ''}.`;
      }
      // A suburb page's own address (like /nsw/sydney/funeral-services/randwick)
      // is valid too — those pages are designed from the Suburb editor, which
      // is also where this shape normally originates.
      const isSuburbOverride =
        segments.length === 4 &&
        segments[2] === 'funeral-services' &&
        this.suburbPaths.includes(segments.join('/'));
      if (isHubOverride || isSuburbOverride) {
        // fall through — valid
      } else if (segments.length !== 3) {
        return "Inside a state, a page must be a region's own address (like /nsw/sydney — it replaces the built-in region page), a suburb page's own address (like /nsw/sydney/funeral-services/randwick — design it from the Suburbs list), or sit directly under a region like /nsw/sydney/your-page.";
      }
      const region = this.regions.find((r) => r.slug === segments[1]);
      if (!isHubOverride && !region) {
        return `"${segments[1]}" isn't one of the site's regions — check the region part of the address against the Regions list.`;
      }
      if (!isHubOverride && region && (region.state || 'nsw').toLowerCase() !== segments[0]) {
        return `The region "${segments[1]}" is in ${(region.state || 'NSW').toUpperCase()}, so this page's address must start with /${(region.state || 'nsw').toLowerCase()}/.`;
      }
    }

    // These subtrees belong to dynamic routes ([slug].astro etc.) — a CMS
    // page under them is silently never built (the dynamic route claims the
    // URL space and the catch-all excludes it), so refuse at the door.
    const DYNAMIC_PREFIXES = ['blog', 'guides', 'venues', 'funeral-directors', 'ceremony', 'charity-partners'];
    if (segments.length >= 2 && DYNAMIC_PREFIXES.includes(segments[0])) {
      return `Addresses under /${segments[0]}/ belong to the ${segments[0]} pages, which are built from their own records — a page here would never appear on the site. Edit the ${segments[0]} content instead, or pick an address outside /${segments[0]}/.`;
    }

    // Slugifies to nothing ("/-", "/---"): the site can't name the page, and
    // its sync refuses the whole snapshot — blocking every publish, not just
    // this page's.
    if (!this.pathKey(path)) {
      return 'That address doesn\'t give the page a usable name — it is only punctuation. Try something like /about/story.';
    }

    for (const other of this.otherPagePaths) {
      if (this.pathKey(other) === this.pathKey(path)) {
        return `Another page already uses the address "${other}", which is the same as "${path}" once it is turned into a publishing name. Give this page a different address.`;
      }
    }
    return null;
  }

  /**
   * Mirrors picaluna-v3's pageSlugFromPath — the publish identity. It applies
   * the SAME slugify (contentSlug) v3 does, so "/about/my page" and
   * "/about/my-page" are recognised as the collision they are.
   */
  private pathKey(path: string): string {
    const trimmed = (path || '').replace(/^\/+|\/+$/g, '');
    return trimmed === '' ? 'home' : contentSlug(trimmed.replace(/\//g, '-'));
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }
}
